import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { FormatDateOptions, useIntl } from "react-intl";
import isPast from "date-fns/isPast";
import parseISO from "date-fns/parseISO";
import addSeconds from "date-fns/addSeconds";
import formatISO from "date-fns/formatISO";
import BookingSection from "../AppointmentBookingSection";
import { useAppointmentBookingContext } from "../AppointmentBookingContext";
import AppointmentLayout from "../AppointmentLayout";
import parseAppointment from "./parseAppointment";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import useTranslations from "./useTranslations";
import getTotalSummary from "./getTotalSummary";
import { setSupabaseAccessToken } from "../../../hooks/useSupabase";
import { useAuthContext } from "../../Auth/AuthContextProvider";
import { SelectedCenterService } from "../AppointmentBookingContext/types";
import {
  setStorageAppointment,
  clearStorageAppointment,
  getStorageCheckoutExpiresAt,
} from "../AppointmentBookingContext/storageAppointmentHelpers";
import useDeviceType from "../../../hooks/useDeviceType";
import {
  GAEventCategory,
  SUPABASE_CENTER_USER_TOKEN_KEY,
} from "../../../common/constants";
import { getFormattedServices } from "./getFormattedServices";
import { useUIContext } from "../../Unknown/UIContext";
import { checkIsCouponSelected } from "./checkIsCouponSelected";

const dateFormatOptions: FormatDateOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

const AppointmentBookingScreen: FC = () => {
  const [isCouponApplying, setIsCouponApplying] = useState(false);
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const { signOut, isCenterUser } = useAuthContext();
  const { formatDate, formatTime } = useIntl();

  useEffect(() => {
    const centerUserSupabaseToken = sessionStorage.getItem(
      SUPABASE_CENTER_USER_TOKEN_KEY,
    );

    if (!centerUserSupabaseToken) {
      signOut();
    }
  }, [signOut]);

  const navigate = useNavigate();

  const { translations } = useTranslations();

  const { isMobile } = useDeviceType();

  const { setAlert } = useUIContext();

  const appointmentBooking = useFirebaseAppFunction("appointmentBooking");
  const applyAppointmentCoupon = useFirebaseAppFunction(
    "applyAppointmentCoupon",
  );
  const onAppointmentExpired = useFirebaseAppFunction("onAppointmentExpired");

  const {
    selectedCenter,
    isAllCenterServicesValid,
    selectedAddOns,
    selectedDate,
    selectedTimeSlot,
    isSubmitLoading,
    selectedCenterServices,
    availableTimeSlots,
    storageAppointmentId,
    overriddenPrice,
    overriddenDeposit,
    overriddenTime,
    addOns,
    coupon,
    couponError,
    setCouponError,
    setCoupon,
    onTimeOverride,
    onPriceOverride,
    onDepositOverride,
    setIsSubmitLoading,
  } = useAppointmentBookingContext();

  const {
    submitDisabledError,
    timeSlotNotAvailableError,
    defaultError,
    invalidCoupon,
    couponAlreadyApplied,
    addOnNotFound,
  } = translations;

  const { updateAvailableTimeSlots } = availableTimeSlots;

  const isSubmitButtonDisabled =
    !selectedCenter ||
    !isAllCenterServicesValid ||
    !selectedDate ||
    isCouponApplying ||
    (selectedTimeSlot === null && !overriddenTime);

  const filteredCenterServices = useMemo(
    () => selectedCenterServices.filter(Boolean) as SelectedCenterService[],
    [selectedCenterServices],
  );

  useEffect(() => {
    const isAddOnCoupon = !!coupon?.addOnId;

    if (!coupon || !isAddOnCoupon) return;

    const isSelected = checkIsCouponSelected({
      coupon,
      selectedCenterServices,
      selectedAddOns,
    });

    if (couponError === addOnNotFound && isSelected) {
      setCouponError(null);
    }

    if (couponError !== addOnNotFound && !isSelected) {
      setCoupon(null);
    }
  }, [
    addOnNotFound,
    coupon,
    coupon?.addOnId,
    couponError,
    selectedAddOns,
    selectedCenterServices,
    setCoupon,
    setCouponError,
  ]);

  const overriddenPriceNumber = overriddenPrice
    ? parseFloat(overriddenPrice)
    : null;

  const overriddenDepositNumber = overriddenDeposit
    ? parseFloat(overriddenDeposit)
    : null;

  const onCouponApply = async () => {
    setIsCouponApplying(true);
    try {
      if (coupon?.amount) {
        setCouponError(couponAlreadyApplied);
        return;
      }

      if (!couponCodeInput) {
        setCouponError(invalidCoupon);
        return;
      }

      const response = await applyAppointmentCoupon({
        couponCode: couponCodeInput,
        appointmentId: storageAppointmentId || undefined,
      });

      if (response.data.status === "error") {
        const { code } = response.data.error;

        switch (code) {
          case "COUPON_ALREADY_APPLIED": {
            setCouponError(couponAlreadyApplied);
            return;
          }
          case "INVALID_COUPON": {
            setCouponError(invalidCoupon);
            return;
          }
          case "ADD_ON_NOT_FOUND": {
            setCouponError(addOnNotFound);
            return;
          }
          default: {
            throw new Error(defaultError);
          }
        }
      }

      if (response.data.status === "success") {
        const { data: couponData } = response.data;

        if (couponData.addOnId) {
          const isSelected = checkIsCouponSelected({
            coupon: couponData,
            selectedCenterServices,
            selectedAddOns,
          });

          if (!isSelected) {
            setCouponError(addOnNotFound);
            return;
          }
        }

        setCouponCodeInput("");
        setCouponError(null);
        setCoupon(couponData);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : defaultError;
      setAlert({ severity: "error", message, isShown: true });
    } finally {
      setIsCouponApplying(false);
    }
  };

  const onSubmitClick = useCallback(async () => {
    const formattedServices = getFormattedServices({
      selectedAddOns,
      selectedCenterServices,
      centerAddOns: addOns.data || [],
    });

    ReactGA.event("Click proceed to checkout", {
      location: selectedCenter?.name,
      selected_scans: formattedServices.services,
      selected_offering_addons: formattedServices.serviceAddOns,
      selected_appointment_addons: formattedServices.appointmentAddOns,
      category: GAEventCategory.click,
      selected_date: selectedTimeSlot
        ? formatDate(selectedTimeSlot, dateFormatOptions)
        : "",
      selected_time: selectedTimeSlot ? formatTime(selectedTimeSlot) : "",
    });

    try {
      if (isSubmitButtonDisabled) {
        throw new Error(submitDisabledError);
      }

      setIsSubmitLoading(true);

      let effectiveUpdateAppointmentId = storageAppointmentId || undefined;

      if (storageAppointmentId) {
        const checkoutExpiresAtStr = getStorageCheckoutExpiresAt();
        if (checkoutExpiresAtStr && isPast(parseISO(checkoutExpiresAtStr))) {
          try {
            await onAppointmentExpired({ appointmentId: storageAppointmentId });
            clearStorageAppointment();
            effectiveUpdateAppointmentId = undefined;
          } catch {
            // Slot still alive (e.g. timer was extended) — proceed with existing appointment
          }
        }
      }

      const appointmentBookingParams = parseAppointment({
        center: selectedCenter,
        centerServices: filteredCenterServices,
        addOns: selectedAddOns,
        dateTime: selectedTimeSlot || selectedDate,
        overriddenTime,
        updateAppointmentId: effectiveUpdateAppointmentId,
        overriddenPrice: overriddenPriceNumber,
        overriddenDeposit: overriddenDepositNumber,
        coupon,
      });

      const response = await appointmentBooking(appointmentBookingParams);

      if (response.data.status === "error") {
        const { code } = response.data.error;

        switch (code) {
          case "TIME_SLOT_NOT_AVAILABLE": {
            await updateAvailableTimeSlots();
            throw new Error(timeSlotNotAvailableError);
          }
          default: {
            throw new Error(defaultError);
          }
        }
      }

      const { data } = response.data;

      const checkoutExpiresAt = selectedCenter
        ? formatISO(
            addSeconds(
              new Date(),
              selectedCenter.appointmentCheckoutDurationSeconds,
            ),
          )
        : undefined;

      setStorageAppointment(
        data.appointmentId,
        isCenterUser ? undefined : data.supabaseToken,
        checkoutExpiresAt,
      );

      if (
        !isCenterUser &&
        !sessionStorage.getItem(SUPABASE_CENTER_USER_TOKEN_KEY)
      ) {
        setSupabaseAccessToken(data.supabaseToken);
      }

      navigate(`/${data.appointmentId}/checkout`);
      return;
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert((error as Error).message);
    } finally {
      setIsSubmitLoading(false);
    }
  }, [
    selectedAddOns,
    selectedCenterServices,
    addOns.data,
    selectedCenter,
    selectedTimeSlot,
    formatDate,
    formatTime,
    isSubmitButtonDisabled,
    setIsSubmitLoading,
    filteredCenterServices,
    selectedDate,
    overriddenTime,
    storageAppointmentId,
    overriddenPriceNumber,
    overriddenDepositNumber,
    coupon,
    appointmentBooking,
    onAppointmentExpired,
    isCenterUser,
    navigate,
    submitDisabledError,
    updateAvailableTimeSlots,
    timeSlotNotAvailableError,
    defaultError,
  ]);

  const {
    depositAmount,
    discountSum,
    couponAmount,
    priceWithDiscount,
    isOverriddenPriceError,
    isOverriddenDepositError,
  } = useMemo(() => {
    const { discountAmount } = selectedCenter || {};
    return getTotalSummary({
      centerServices: filteredCenterServices || [],
      addOns: selectedAddOns || [],
      discountAmount,
      overriddenPrice: overriddenPriceNumber,
      overriddenDeposit: overriddenDepositNumber,
      centerAddOns: addOns.data || [],
      coupon,
    });
  }, [
    selectedCenter,
    filteredCenterServices,
    selectedAddOns,
    overriddenPriceNumber,
    overriddenDepositNumber,
    addOns,
    coupon,
  ]);

  const buttonSubmitText = isMobile
    ? translations.buttonMobileSubmitText
    : translations.buttonDesktopSubmitText;

  return (
    <AppointmentLayout
      selectedCenter={selectedCenter}
      selectedDateTime={selectedTimeSlot}
      selectedCenterServices={filteredCenterServices}
      selectedAddOns={selectedAddOns}
      onSubmitClick={onSubmitClick}
      isSubmitButtonDisabled={isSubmitButtonDisabled}
      isSubmitLoading={isSubmitLoading}
      depositAmount={depositAmount}
      discountSum={discountSum}
      priceWithDiscount={priceWithDiscount}
      buttonSubmitText={buttonSubmitText}
      isCenterUser={isCenterUser}
      overriddenPrice={overriddenPrice}
      overriddenDeposit={overriddenDeposit}
      centerAddOns={addOns.data || []}
      onPriceOverride={onPriceOverride}
      onDepositOverride={onDepositOverride}
      onTimeOverride={onTimeOverride}
      overriddenTime={overriddenTime}
      isDaySelected={!!selectedDate}
      onCouponApply={onCouponApply}
      isOverriddenPriceError={isOverriddenPriceError}
      isOverriddenDepositError={isOverriddenDepositError}
      isCouponDisabled={isSubmitButtonDisabled}
      couponCodeInput={couponCodeInput}
      couponError={couponError}
      setCouponError={setCouponError}
      setCouponCodeInput={setCouponCodeInput}
      couponAmount={couponAmount}
      couponCode={coupon?.code}
    >
      <BookingSection />
    </AppointmentLayout>
  );
};

export default AppointmentBookingScreen;
